import { BOOKING_URL } from "@/config/site";

const approaches = [
  {
    abbr: "CBT",
    name: "Cognitive Behavioral Therapy",
    description: "Helps identify and shift unhelpful thought patterns that contribute to anxiety, depression, and stress. CBT enhances the strength-based approach while giving clients a sense of self-involvement and worth.",
  },
  {
    abbr: "MBI",
    name: "Mindfulness-Based Interventions",
    description: "Incorporates present-moment awareness to reduce rumination, manage stress, and cultivate a deeper connection with yourself. Mindfulness works hand-in-hand with the strength-based model.",
  },
  {
    abbr: "SFT",
    name: "Solution-Focused Therapy",
    description: "Keeps the focus on where you want to go rather than dwelling on problems. Together we identify obtainable goals and build practical steps to reach them.",
  },
  {
    abbr: "PCT",
    name: "Person-Centered Therapy",
    description: "Rooted in the belief that you are in charge of your sessions, your goals, and the direction therapy takes. Techniques are customized to each individual client.",
  },
];

const firstSession = [
  "A warm, welcoming conversation — no pressure, no judgment",
  "Exploration of your goals and what you hope to achieve",
  "An introduction to the strength-based, person-centered approach",
  "A collaborative plan tailored entirely to you",
  "A stronger sense of self-care and motivation from day one",
];

export default function ApproachSection() {
  return (
    <section className="bg-[#f0f9f9] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          {/* Left */}
          <div className="lg:sticky lg:top-28">
            <p className="text-[#9a9a9a] text-xs font-medium tracking-widest uppercase mb-4">Therapeutic Approach</p>
            <h2 className="text-3xl md:text-4xl font-light text-[#2d2d2d] leading-snug mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
              Evidence-based care,<br />
              <span className="italic font-semibold">tailored to you.</span>
            </h2>
            <p className="text-[#6b6b6b] text-sm leading-relaxed mb-8">
              I use techniques customized to each client. Through these methods, I strive for a sense of comfort and trust — because the client should always feel in control of their experience and their outcomes.
            </p>

            {/* First Session Box */}
            <div className="bg-white rounded-2xl p-6 border border-[#c8e8e6] mb-6">
              <p className="text-[#2d2d2d] text-sm font-semibold mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                What to expect in your first session
              </p>
              <ul className="flex flex-col gap-3">
                {firstSession.map((item) => (
                  <li key={item} className="flex items-start gap-3 text-sm text-[#5a5a5a]">
                    <span className="w-5 h-5 flex items-center justify-center rounded-full bg-[#e6f6f5] flex-shrink-0 mt-0.5">
                      <i className="ri-check-line text-[#4db8b1] text-xs"></i>
                    </span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-2xl overflow-hidden h-52">
              <img
                src="/images/approach1b.jpg"
                alt="Therapy session environment"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>

          {/* Right: Approach Cards */}
          <div className="flex flex-col gap-4">
            <p className="text-[#9a9a9a] text-xs font-medium tracking-widest uppercase mb-1">Methods I Use</p>
            {approaches.map((approach) => (
              <div key={approach.abbr} className="bg-white rounded-xl p-6 border border-[#d0ecea] flex gap-5 items-start">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#e6f6f5] flex-shrink-0">
                  <span className="text-[#418b86] text-xs font-bold">{approach.abbr}</span>
                </div>
                <div>
                  <h3 className="text-[#2d2d2d] font-semibold text-sm mb-2">{approach.name}</h3>
                  <p className="text-[#6b6b6b] text-sm leading-relaxed">{approach.description}</p>
                </div>
              </div>
            ))}

            {/* Who I Serve */}
            <div className="mt-2 bg-white rounded-xl p-6 border border-[#d0ecea]">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#e6f6f5]">
                  <i className="ri-team-line text-[#4db8b1] text-lg"></i>
                </div>
                <p className="text-[#2d2d2d] font-semibold text-sm" style={{ fontFamily: "'Playfair Display', serif" }}>Who I Work With</p>
              </div>
              <p className="text-[#6b6b6b] text-sm leading-relaxed mb-3">
                I work with ages 6 and up, with a focus on adolescents, teens, and adults. My background includes working with foster care children, veterans, couples, and individuals across a wide variety of settings.
              </p>
              <p className="text-[#6b6b6b] text-sm leading-relaxed">
                The ideal client is one who has goals in mind with the resolve to work toward them in a comfortable and accepting environment.
              </p>
            </div>

            <div className="mt-2 bg-[#418b86] rounded-xl p-7 text-white">
              <p className="text-white/85 text-sm leading-relaxed mb-5">
                Ready to take the first step? A free consultation is the perfect way to see if we're a good fit — no pressure, no commitment.
              </p>
              <a
                href={BOOKING_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#7ec8e3] hover:bg-white text-[#418b86] text-sm font-medium px-6 py-3 rounded-full transition-all cursor-pointer whitespace-nowrap"
              >
                Book a Free Consultation
                <i className="ri-arrow-right-line"></i>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
